import { CUSTOMER_TYPE, PAYMENT_METHOD, RECIPIENT_TYPES, PAYMENT_PROVIDERS } from 'components/common/constants';
import { replaceSpaces, strToFloat, trimTextContent, isCryptoProvider } from 'services/utils';

// Universal model that works for all provider types and scenarios
export default function CreateNewPaymentFormModel({ providerTableData, data }) {
  // For template creation (when called from ReviewAndSaveNewPaymentFormStep without providerTableData)
  if (!providerTableData && !data.wallet) {
    return createTemplateModel(data);
  }

  // For transaction validation (when called from validateTransfer/validateCryptoTransfer)
  const wallet = data.wallet;
  const transferProvider = wallet?.transfer_provider;
  const paymentMethod = data.paymentMethod;

  // Crypto provider validation
  if (isCryptoProvider(transferProvider)) {
    return createCryptoModel({ providerTableData, data });
  }

  // Clear Junction provider validation
  if (transferProvider === PAYMENT_PROVIDERS.CLEARJUNCTION) {
    return createClearJunctionModel({ providerTableData, data });
  }

  // All other providers (OpenPayd, Manual, etc.)
  const isAdvanced = paymentMethod === PAYMENT_METHOD.SWIFT || paymentMethod === PAYMENT_METHOD.TARGET2;
  return isAdvanced
    ? createOpenPaydAdvancedModel({ providerTableData, data })
    : createOpenPaydSimpleModel({ providerTableData, data });
}

// Template creation model (original logic)
function createTemplateModel(data) {
  const requestData = {
    newPaymentFormName: data?.newPaymentFormName,
    amount: data?.amount,
    currency: data?.currency,
    paymentMethod: data?.paymentMethod
  };

  const requisites = {};
  if (
    [PAYMENT_METHOD.SEPA, PAYMENT_METHOD.SEPA_INSTANT, PAYMENT_METHOD.SWIFT].includes(data?.paymentMethod) &&
    data?.accountNumber
  ) {
    requisites.iban = data?.accountNumber;
  }

  if (
    [PAYMENT_METHOD.FASTER_PAYMENTS, PAYMENT_METHOD.BACS, PAYMENT_METHOD.CHAPS, PAYMENT_METHOD.SWIFT].includes(
      data?.paymentMethod
    ) &&
    data?.accountNumber
  ) {
    requisites.accountNumber = data?.accountNumber;
    requisites.iban = data?.iban;
    requisites.cryptoAddress = data?.cryptoAddress;
    requisites.cryptoNetwork = data?.cryptoNetwork;
  }

  if ([PAYMENT_METHOD.INTERNAL].includes(data?.paymentMethod) && data?.accountNumber) {
    requisites.accountNumber = data?.accountNumber;
    requisites.cryptoAddress = data?.cryptoAddress;
    requisites.cryptoNetwork = data?.cryptoNetwork;
  }

  if (
    [PAYMENT_METHOD.FASTER_PAYMENTS, PAYMENT_METHOD.BACS, PAYMENT_METHOD.CHAPS].includes(data?.paymentMethod) &&
    data?.sortCode
  )
    requisites.sortCode = data?.sortCode;
  if (data.paymentMethod === PAYMENT_METHOD.SWIFT && data?.bankCountry) requisites.bankCountry = data?.bankCountry;
  if (data.paymentMethod === PAYMENT_METHOD.SWIFT && data?.bankName) requisites.bankName = data?.bankName;
  if (data.paymentMethod === PAYMENT_METHOD.SWIFT && data?.bic) requisites.bic = data?.bic;
  if (data.paymentMethod === PAYMENT_METHOD.CRYPTO && data?.accountNumber)
    requisites.cryptoAddress = data?.accountNumber;
  if (data.paymentMethod === PAYMENT_METHOD.CRYPTO && data?.network) requisites.cryptoNetwork = data?.network;

  const beneficiary = {
    type: data?.type || CUSTOMER_TYPE[data?.recipientType?.toUpperCase()]?.toUpperCase(),
    registrationNumber: data?.registrationNumber,
    city: data?.city,
    country: data?.country,
    postCode: data?.postCode,
    address: data?.address
  };
  if (data?.recipientType === RECIPIENT_TYPES.COMPANY && data?.companyName) beneficiary.companyName = data?.companyName;
  if (data?.recipientType === RECIPIENT_TYPES.INDIVIDUAL && data?.firstName) beneficiary.firstName = data?.firstName;
  if (data?.recipientType === RECIPIENT_TYPES.INDIVIDUAL && data?.lastName) beneficiary.lastName = data?.lastName;

  if (data?.isCryptoPayment) {
    requestData.beneficiary = {
      type: RECIPIENT_TYPES.INDIVIDUAL.toUpperCase(),
      firstName: 'Friendly',
      lastName: 'Recipient'
    };
  } else {
    requestData.comment = data?.transferDetails;
    requestData.beneficiary = beneficiary;
  }

  requestData.requisites = requisites;
  return requestData;
}

// Crypto model
function createCryptoModel({ providerTableData, data }) {
  return {
    paymentMethod: data.paymentMethod || null,
    senderWallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    recipient: {
      walletAddress: providerTableData.recipient_wallet || data.wallet_address,
      network: (data.wallet && data.wallet?.network?.trim()) || ''
    },
    comment: trimTextContent(providerTableData?.comment) || null,
    documents: data.uploadedFiles ? data.uploadedFiles : null
  };
}

// Clear Junction model
function createClearJunctionModel({ providerTableData, data }) {
  return {
    sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    transaction_recipient: {
      beneficiary: {
        type: providerTableData.recipient_type.toUpperCase(),
        ...(providerTableData.recipient_type === 'Company'
          ? {
              company_name: trimTextContent(providerTableData?.company_name) || ''
            }
          : {
              first_name: trimTextContent(providerTableData?.first_name) || '',
              last_name: trimTextContent(providerTableData?.last_name) || ''
            })
      },
      requisites: providerTableData?.recipient_wallet
        ? {
            iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
            bank_country: providerTableData?.bank_country?.trim() || data?.bank_country?.trim()
          }
        : {
            account_number: providerTableData?.account_number?.trim(),
            sort_code: providerTableData?.sort_code?.trim().replace(/-/g, ''),
            bank_country: providerTableData?.bank_country?.trim() || process.env.REACT_APP_DEFAULT_COUNTRY_CODE
          }
    },
    payment_method: data.paymentMethod || '',
    transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
    comment: trimTextContent(providerTableData?.comment) || '',
    documents: data.uploadedFiles ? data.uploadedFiles : []
  };
}

// OpenPayd Simple model
function createOpenPaydSimpleModel({ providerTableData, data }) {
  return {
    sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    transaction_recipient: {
      beneficiary: {
        type: providerTableData.recipient_type.toUpperCase(),
        ...(providerTableData.recipient_type === 'Company'
          ? {
              company_name: trimTextContent(providerTableData?.company_name) || ''
            }
          : {
              first_name: trimTextContent(providerTableData?.first_name) || '',
              last_name: trimTextContent(providerTableData?.last_name) || ''
            })
      },
      requisites:
        providerTableData?.recipient_wallet &&
        data.paymentMethod === PAYMENT_METHOD.INTERNAL &&
        !providerTableData?.sort_code
          ? {
              wallet_number: replaceSpaces(providerTableData?.recipient_wallet) || '',
              bank_country: providerTableData?.bank_country?.trim() || data?.bank_country?.trim()
            }
          : providerTableData?.recipient_wallet &&
              data.paymentMethod !== PAYMENT_METHOD.INTERNAL &&
              !providerTableData?.sort_code
            ? {
                iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
                bank_country: providerTableData?.bank_country?.trim() || data?.bank_country?.trim()
              }
            : {
                account_number: providerTableData?.account_number?.trim(),
                sort_code: providerTableData?.sort_code?.trim().replace(/-/g, ''),
                bank_country: providerTableData?.bank_country?.trim() || process.env.REACT_APP_DEFAULT_COUNTRY_CODE
              }
    },
    payment_method: data.paymentMethod || '',
    transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
    comment: trimTextContent(providerTableData?.comment) || '',
    documents: data.uploadedFiles ? data.uploadedFiles : []
  };
}

// OpenPayd Advanced model (for SWIFT/TARGET2)
function createOpenPaydAdvancedModel({ providerTableData, data }) {
  return {
    sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    transaction_recipient: {
      registration_number: providerTableData.registration_number,
      beneficiary_city: providerTableData.beneficiary_city,
      beneficiary_country: providerTableData.beneficiary_country,
      beneficiary_post_code: providerTableData.beneficiary_post_code,
      beneficiary_address: providerTableData.beneficiary_address,
      beneficiary: {
        type: providerTableData.recipient_type.toUpperCase(),
        ...(providerTableData.recipient_type === 'Company'
          ? {
              company_name: trimTextContent(providerTableData?.company_name) || ''
            }
          : {
              first_name: trimTextContent(providerTableData?.first_name) || '',
              last_name: trimTextContent(providerTableData?.last_name) || ''
            })
      },
      requisites:
        data.paymentMethod === PAYMENT_METHOD.SWIFT || data.paymentMethod === PAYMENT_METHOD.TARGET2
          ? {
              iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
              bank_country: data?.bank_country?.trim() || providerTableData?.bank_country?.trim(),
              bank_name: data?.bank_name?.trim() || providerTableData?.bank_name?.trim(),
              account_number: data?.account_number?.trim() || providerTableData?.account_number?.trim(),
              bic: data?.bic?.trim() || providerTableData?.bic?.trim()
            }
          : {
              iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
              bank_country: data?.bank_country?.trim()
            }
    },
    payment_method: data.paymentMethod || '',
    transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
    comment: trimTextContent(providerTableData?.comment) || '',
    documents: data.uploadedFiles ? data.uploadedFiles : []
  };
}
